import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Grid, Chip, Divider,
  TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import {
  VpnKey, CheckCircle, Business, CalendarMonth, Person,
  VerifiedUser, Support, Edit, Refresh, PersonOff, People,
} from '@mui/icons-material';
import { authService, licenseConfigService } from '../../services/api';
import TopBar from '../../components/TopBar';

const InfoRow = ({ label, value, chip, chipColor }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
    {chip
      ? <Chip label={value} color={chipColor || 'success'} size="small" icon={<CheckCircle />} />
      : <Typography variant="body2" fontWeight={600}>{value}</Typography>
    }
  </Box>
);

const IST = { timeZone: 'Asia/Kolkata' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', ...IST }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', ...IST }) : '—';

const LicensingPage = () => {
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [terminating, setTerminating] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ maxConcurrentUsers: 5, licensedTo: '', licenseKey: '', validUntil: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const res = await licenseConfigService.get();
      setConfig(res.data);
    } catch {}
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await authService.getSessions();
      setSessions(res.data);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadConfig(), loadSessions()]).finally(() => setLoading(false));
    const id = setInterval(loadSessions, 15_000); // refresh sessions every 15 s
    return () => clearInterval(id);
  }, [loadConfig, loadSessions]);

  const openEdit = () => {
    setEditForm({
      maxConcurrentUsers: config?.maxConcurrentUsers ?? 5,
      licensedTo: config?.licensedTo ?? '',
      licenseKey: config?.licenseKey ?? '',
      validUntil: config?.validUntil ? config.validUntil.split('T')[0] : '',
    });
    setSaveError('');
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editForm.maxConcurrentUsers || editForm.maxConcurrentUsers < 1) {
      setSaveError('Max concurrent users must be at least 1.');
      return;
    }
    setSaving(true);
    try {
      await licenseConfigService.update({
        maxConcurrentUsers: parseInt(editForm.maxConcurrentUsers),
        licensedTo: editForm.licensedTo,
        licenseKey: editForm.licenseKey,
        validUntil: editForm.validUntil ? new Date(editForm.validUntil).toISOString() : null,
      });
      await loadConfig();
      setEditOpen(false);
    } catch {
      setSaveError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const terminateSession = async (sessionId) => {
    setTerminating(sessionId);
    try {
      await authService.terminateSession(sessionId);
      await loadSessions();
    } catch {}
    setTerminating(null);
  };

  const licenseValid = config?.validUntil ? new Date(config.validUntil) > new Date() : true;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <TopBar title="Licensing" onBack={() => navigate('/dashboard')} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress size={48} />
        </Box>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Licensing" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* Header */}
        <Paper elevation={4} sx={{ p: 4, borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #37474f 0%, #546e7a 100%)', color: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <VpnKey sx={{ fontSize: 52 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>Hospital Management System</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>Software License & Subscription Details</Typography>
              </Box>
            </Box>
            <Button variant="outlined" startIcon={<Edit />} onClick={openEdit}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', '&:hover': { borderColor: '#fff' } }}>
              Edit License
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={3}>

          {/* Concurrent Sessions Card */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%', border: '2px solid', borderColor: sessions.length >= (config?.maxConcurrentUsers ?? 5) ? '#ef9a9a' : '#a5d6a7' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People sx={{ color: '#1565c0' }} />
                  <Typography variant="h6" fontWeight={700} color="#1565c0">Active Sessions</Typography>
                </Box>
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={loadSessions}><Refresh fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h2" fontWeight={800} color={sessions.length >= (config?.maxConcurrentUsers ?? 5) ? '#c62828' : '#2e7d32'}>
                  {sessions.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of {config?.maxConcurrentUsers ?? 5} allowed
                </Typography>
                <Chip
                  sx={{ mt: 1 }}
                  label={sessions.length >= (config?.maxConcurrentUsers ?? 5) ? 'Limit Reached' : 'Capacity Available'}
                  color={sessions.length >= (config?.maxConcurrentUsers ?? 5) ? 'error' : 'success'}
                  size="small"
                />
              </Box>
            </Paper>
          </Grid>

          {/* License Info */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VerifiedUser sx={{ color: '#37474f' }} />
                <Typography variant="h6" fontWeight={700} color="#37474f">License</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="License Status" value={licenseValid ? 'Active' : 'Expired'} chip chipColor={licenseValid ? 'success' : 'error'} />
              <InfoRow label="License Key" value={config?.licenseKey || 'HMS-PRO-2026-VEDANTA'} />
              <InfoRow label="Max Concurrent Users" value={config?.maxConcurrentUsers ?? 5} />
              <InfoRow label={config?.validUntil && new Date(config.validUntil) < new Date() ? 'Expired On' : 'Valid Until'} value={config?.validUntil ? fmtDate(config.validUntil) : 'Unlimited'} />
            </Paper>
          </Grid>

          {/* Hospital Info */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business sx={{ color: '#1565c0' }} />
                <Typography variant="h6" fontWeight={700} color="#1565c0">Licensed To</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <InfoRow label="Hospital Name" value={config?.licensedTo || 'Vedanta Hospital'} />
              <InfoRow label="Contact Email" value="badheypratik@gmail.com" />
              <InfoRow label="Contact Phone" value="+91 9960880094" />
              <InfoRow label="Build Version" value="1.0.0 (.NET 8 / React 18)" />
            </Paper>
          </Grid>

          {/* Active Sessions Table */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ color: '#1565c0' }} />
                  <Typography variant="h6" fontWeight={700} color="#1565c0">Currently Logged In Users</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {sessionsLoading && <CircularProgress size={16} />}
                  <Typography variant="caption" color="text.secondary">Auto-refreshes every 15 s</Typography>
                  <Tooltip title="Refresh now">
                    <IconButton size="small" onClick={loadSessions}><Refresh fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {sessions.length === 0 ? (
                <Alert severity="info">No active sessions right now.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><b>User</b></TableCell>
                        <TableCell><b>Role</b></TableCell>
                        <TableCell><b>Login Time</b></TableCell>
                        <TableCell><b>Last Activity</b></TableCell>
                        <TableCell><b>IP Address</b></TableCell>
                        <TableCell align="center"><b>Terminate</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions.map(s => (
                        <TableRow key={s.sessionId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{s.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary">{s.username}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={s.role} size="small" color={s.role === 'Admin' ? 'primary' : s.role === 'Doctor' ? 'success' : 'default'} variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{fmtDate(s.createdAt)}</Typography><br />
                            <Typography variant="caption" color="text.secondary">{fmtTime(s.createdAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{fmtTime(s.lastActivityAt)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{s.ipAddress || '—'}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Force logout this user">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={terminating === s.sessionId}
                                onClick={() => terminateSession(s.sessionId)}
                              >
                                {terminating === s.sessionId
                                  ? <CircularProgress size={16} color="error" />
                                  : <PersonOff fontSize="small" />
                                }
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Modules */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircle sx={{ color: '#2e7d32' }} />
                <Typography variant="h6" fontWeight={700} color="#2e7d32">Active Modules</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {[
                  'OPD Management', 'IPD Management', 'Patient 360°',
                  'Prescription Builder', 'Lab Reports', 'Billing & Invoicing',
                  'Appointments', 'Attendance', 'Leave Management',
                  'Salary Processing', 'Analytics & Reports', 'Medical Certificates',
                  'OBG / EDD Calendar', 'OPD Display Screen', 'Cabin Assignment',
                  'Concurrent Session Control',
                ].map(mod => (
                  <Chip key={mod} label={mod} size="small" color="primary" variant="outlined"
                    icon={<CheckCircle />} sx={{ fontWeight: 500 }} />
                ))}
              </Box>
            </Paper>
          </Grid>

        </Grid>
      </Container>

      {/* Edit License Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Edit License Configuration</DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Max Concurrent Users *" type="number"
                inputProps={{ min: 1, max: 1000 }}
                value={editForm.maxConcurrentUsers}
                onChange={e => setEditForm(f => ({ ...f, maxConcurrentUsers: e.target.value }))}
                helperText="Number of users who can be logged in simultaneously"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Licensed To (Hospital Name)"
                value={editForm.licensedTo}
                onChange={e => setEditForm(f => ({ ...f, licensedTo: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="License Key"
                value={editForm.licenseKey}
                onChange={e => setEditForm(f => ({ ...f, licenseKey: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Valid Until" type="date"
                InputLabelProps={{ shrink: true }}
                value={editForm.validUntil}
                onChange={e => setEditForm(f => ({ ...f, validUntil: e.target.value }))}
                helperText="Leave blank for unlimited validity"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LicensingPage;
